import React, { useState, useCallback, useRef, useEffect } from "react";

/**
 * A custom React hook to provide copy-to-clipboard functionality for multiple distinct items,
 * with independent copied states and timers for each item.
 *
 * It returns a function `getCopyHandler` which, when called with a unique ID,
 * returns a specific `[copied, copy]` pair for that ID.
 *
 * @returns {Function} getCopyHandler
 * - getCopyHandler(id): A function that returns [copied, copy] for a given ID.
 * - copied: A boolean indicating whether the content associated with 'id' is currently copied (true for 2 seconds).
 * - copy: A function that takes the text to copy as an argument for the given ID.
 */
const useClipboard = () => {
  // State to store the copied status for each item by its ID.
  // Example: { 'accountNumber': true, 'amount': false }
  const [copiedStates, setCopiedStates] = useState({});

  // useRef to store timeout IDs. This allows us to clear specific timers
  // without causing re-renders when the timers are set or cleared.
  const timersRef = useRef({});

  // The core copy logic, memoized with useCallback.
  // It takes the unique 'id' of the item and the 'textToCopy'.
  const copy = useCallback((id, textToCopy) => {
    // If there's an existing timer for this specific 'id', clear it.
    // This prevents a previous copy action for the same ID from prematurely
    // resetting its 'copied' state if the user clicks it again quickly.
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
    }

    // Create a temporary textarea element to hold the text for copying.
    // This is a common workaround for `document.execCommand('copy')` to work reliably.
    const textarea = document.createElement("textarea");
    textarea.value = textToCopy;
    textarea.style.position = "fixed"; // Prevents scrolling to bottom of page
    textarea.style.opacity = "0"; // Hides the textarea from view
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select(); // Selects the text in the textarea

    try {
      // Execute the copy command. This is the deprecated but widely compatible method
      // used due to potential iframe restrictions with navigator.clipboard.writeText().
      const successful = document.execCommand("copy");
      if (successful) {
        // Update the state to mark this specific 'id' as copied.
        // We use a functional update to ensure we're working with the latest state.
        setCopiedStates((prev) => ({ ...prev, [id]: true }));

        // Set a timeout to reset the 'copied' state for this specific 'id' after 2 seconds.
        const timerId = setTimeout(() => {
          setCopiedStates((prev) => ({ ...prev, [id]: false }));
          // Clean up the timer ID from the ref once it has fired.
          delete timersRef.current[id];
        }, 2000);
        // Store the timer ID in the ref, associated with the unique 'id'.
        timersRef.current[id] = timerId;
      } else {
        console.error("Failed to copy text.");
      }
    } catch (err) {
      console.error("Error copying text:", err);
    } finally {
      // Always remove the temporary textarea from the DOM.
      document.body.removeChild(textarea);
    }
  }, []); // Empty dependency array: 'copy' function is created once.

  // This function is what the consumer of the hook will call to get
  // the 'copied' status and the 'copy' function for a specific item ID.
  const getCopyHandler = useCallback(
    (id) => {
      // Determine the copied status for this specific 'id'. Default to false if not set.
      const isCopied = copiedStates[id] || false;
      // Create a specific handler function that calls the main 'copy' function with its 'id'.
      const handleCopy = (textToCopy) => copy(id, textToCopy);

      return [isCopied, handleCopy];
    },
    [copiedStates, copy],
  ); // Dependencies: re-create if copiedStates or the main 'copy' function changes.

  // useEffect for cleanup: clear all active timers when the component using the hook unmounts.
  useEffect(() => {
    return () => {
      // Iterate over all stored timer IDs and clear them.
      for (const timerId of Object.values(timersRef.current)) {
        clearTimeout(timerId);
      }
      // Clear the ref itself.
      timersRef.current = {};
    };
  }, []); // Empty dependency array: runs only on mount and unmount.

  return getCopyHandler;
};

export default useClipboard;
